import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { FoodForm, FoodFormValues } from '~/components/FoodForm';
import Page from '~/components/Shared/Layout/Page/Page';
import Heading from '~/components/Shared/Heading/Heading';
import * as Tabs from '@radix-ui/react-tabs';
import cn from 'classnames';
import styles from './CreateFood.module.scss';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { AuthenticationContext } from '~/components/Authentication/AuthenticationProvider';
import {
  checkIfUserAssetAdmin,
  checkIfUserSuperAdmin,
} from '~/utils/getUserRole';
import { useTranslation } from 'react-i18next';
import { getDays } from '~/utils/getDays';

const CreateFood = (): JSX.Element => {
  const { user } = useContext(AuthenticationContext);
  const isAssetEditable = checkIfUserSuperAdmin(user?.data.role_name);
  const isAssetAdmin = checkIfUserAssetAdmin(user?.data.role_name);
  const [activeTab, setActiveTab] = useState('tab1');
  const navigate = useNavigate();
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const { t } = useTranslation();

  const days = getDays();
  const daysArray = days.map(item => item.type);

  const client = useQueryClient();

  const { mutateAsync: createFood } = useMutation(
    async ({
      name,
      description,
      food_accommodation,
      address,
      assets,
      photos,
      is_active,
      pointObject,
      timezone,
      box_office,
      payment_type,
      isAllAccomodation,
      price_list_code,
      containers_enabled,
      cooking_time,
      folder,
      popup_text,
      popup_is_active,
      limit_hour,
      available_kso_order,
      enabled_scanner_barcode,
      days,
      working_days_all,
      food_types,
      available_order_today,
    }: FoodFormValues) => {
      const payment_type_ids = payment_type.map(item => item.id);
      const accommodation_ids = food_accommodation.map(item => item.id);
      const selectedDays = days.map(item => item.type);
      const available_day_of_week = working_days_all ? daysArray : selectedDays;
      const food_type_ids = food_types.map(item => item.id);

      const response = await instance.post('food-admin', {
        name,
        description,
        ...(isAllAccomodation ? {} : { accommodation_ids }),
        timezone: timezone.id,
        address,
        ...(is_active && { is_active: is_active?.id }),
        ...(isAllAccomodation ? { accommodation_all: 1 } : {}),
        asset_id: assets.id,
        photo_ids: photos?.length ? photos.map(photo => photo.id) : photos,
        object_type_id: pointObject.id,
        box_office_id: box_office.id,
        payment_type_ids,
        price_list_code: price_list_code.name,
        containers_enabled: containers_enabled.id,
        ...(pointObject.id === 2 && { cooking_time: Number(cooking_time) }),
        ...(folder.id ? { folder_id: folder.id } : {}),
        popup_text,
        popup_is_active,
        limit_hour: limit_hour,
        ...((pointObject.id === 1 || pointObject.id === 2) && {
          available_kso_order: available_kso_order.id,
        }),
        enabled_scanner_barcode: enabled_scanner_barcode.id,
        available_day_of_week,
        food_type_ids,
        available_order_today: available_order_today.id,
      });
      return response.data;
    },
    {
      onSuccess: (data: FoodFormValues) => {
        client.setQueryData(['food'], (oldFood?: FoodFormValues[]) => {
          if (!oldFood) {
            return [data];
          }
          return [...oldFood, data];
        });
        toastSuccess(t('new_food_point_added'));
        navigate(`/food`);
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  return (
    <Page heading={<Heading text={t('creating_food_point')} />}>
      <Tabs.Root
        className={styles.tabsRoot}
        value={activeTab}
        onValueChange={setActiveTab}
      >
        <Tabs.List className={styles.tabsList} aria-label="Manage your account">
          <Tabs.Trigger
            value="tab1"
            className={cn(styles.tabsTrigger, {
              [styles.tabsTriggerActive]: activeTab === 'tab1',
            })}
          >
            {t('general_information')}
          </Tabs.Trigger>
        </Tabs.List>
        <Tabs.Content
          tabIndex={undefined}
          className={styles.tabsContent}
          value="tab1"
        >
          <FoodForm
            isAssetEditAvailable={isAssetEditable}
            onSubmit={values => {
              createFood(values);
            }}
            storageKey="create-food"
            hasAvailable={isAssetEditable || isAssetAdmin}
          />
        </Tabs.Content>
      </Tabs.Root>
    </Page>
  );
};

export default CreateFood;
